/**
 * HrFocusedDashboard — дашборд для ролей HR / business_assistant / assistant.
 *
 * Отличия от рекрутингового Dashboard:
 * - НЕТ воронки и таблицы кандидатов.
 * - Сотрудники по отделам — карточками (не только баром).
 * - Отдельный блок «На испытательном сроке» с разбивкой по отделам.
 * - Календарь ближайших дней рождений (без уведомлений — только визуал).
 * - Срочные документы (визы/пермиты) остаются.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    Users, Building2, GraduationCap, Cake, RefreshCw, Plus, Clock,
    AlertTriangle, FileText, XCircle, AlertCircle, CheckCircle, ArrowRight,
    Calendar as CalendarIcon, ChevronRight,
} from 'lucide-react'
import LoadingIcon from '../LoadingIcon'
import KpiTile from '../training/KpiTile'
import AiDailyBriefWidget from './AiDailyBriefWidget'
import { useAuth } from '../../contexts/AuthContext'
import { Widget, OfflineBanner, daysUntil, formatDate, shortName } from './DashboardWidgets'
import { displayName } from '../../utils/nameHelpers'

// AI-брифинг видят только управленческие роли (item 4 — «только HR и админ»).
const AI_BRIEF_ROLES = ['admin', 'hr', 'owner', 'ceo']
import {
    hrDashboard,
    docRecords as docRecordsApi,
    employees as employeesApi,
    fetchWithFallback,
} from '../../api/client'

const MONTH_LABEL_RU = [
    'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
    'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
]

function urgencyOf(days) {
    if (days < 0) return 'expired'
    if (days <= 30) return 'urgent'
    if (days <= 90) return 'warning'
    return 'ok'
}
function statusTextOf(days) {
    if (days < 0) return 'ПРОСРОЧЕН'
    return `${days} дн.`
}
function typeLabelOf(type) {
    if (type === 'visa') return 'Виза'
    if (type === 'permit') return 'Пермит'
    return type
}

function formatBirthdayLabel(daysUntil) {
    if (daysUntil === 0) return 'сегодня'
    if (daysUntil === 1) return 'завтра'
    if (daysUntil < 5) return `через ${daysUntil} дн`
    if (daysUntil < 21) return `через ${daysUntil} дн`
    return `через ${daysUntil} дн`
}

function formatBirthdayDate(mdStr) {
    if (!mdStr) return ''
    const [m, d] = mdStr.split('-')
    return `${parseInt(d, 10)} ${MONTH_LABEL_RU[parseInt(m, 10) - 1] || ''}`.toLowerCase()
}

// #28 — возраст на ближайший ДР (для подсветки юбилеев).
function turningAge(dob, daysUntil) {
    if (!dob) return null
    const birth = new Date(dob)
    if (Number.isNaN(birth.getTime())) return null
    const bday = new Date()
    bday.setHours(0, 0, 0, 0)
    bday.setDate(bday.getDate() + (daysUntil || 0))
    return bday.getFullYear() - birth.getFullYear()
}
function isMilestoneAge(age) {
    return age != null && age >= 20 && age % 5 === 0 // 20, 25, 30… — юбилей
}

function getInitials(name) {
    if (!name) return '?'
    const parts = name.trim().split(/\s+/)
    return (parts[0]?.[0] || '') + (parts[1]?.[0] || '')
}


export default function HrFocusedDashboard() {
    const navigate = useNavigate()
    const { user } = useAuth()
    const [loading, setLoading] = useState(true)
    const [refreshing, setRefreshing] = useState(false)
    const [overview, setOverview] = useState(null)
    const [birthdays, setBirthdays] = useState(null)
    const [docsList, setDocsList] = useState([])
    const [employeesList, setEmployeesList] = useState([])
    const [dataSource, setDataSource] = useState('loading')
    const [birthdayWindow, setBirthdayWindow] = useState(60)

    const loadData = useCallback(async () => {
        let mainSource = 'offline'
        try {
            const [ovRes, bdRes] = await Promise.all([
                hrDashboard.employeesOverview().catch(() => null),
                hrDashboard.birthdays({ days: birthdayWindow }).catch(() => null),
            ])
            if (ovRes) setOverview(ovRes)
            if (bdRes) setBirthdays(bdRes)
            mainSource = ovRes || bdRes ? 'api' : 'offline'
        } catch {
            mainSource = 'offline'
        }

        try {
            const docsResult = await fetchWithFallback(() => docRecordsApi.list(), 'dashboard_documents')
            const docsRaw = docsResult.data?.items || docsResult.data || []
            const arr = Array.isArray(docsRaw) ? docsRaw : []
            if (arr.length > 0) setDocsList(arr)
            if (docsResult.source === 'api') mainSource = 'api'
            else if (mainSource !== 'api' && docsResult.source === 'cache') mainSource = 'cache'
        } catch { /* docs пусто */ }

        try {
            const empResult = await fetchWithFallback(() => employeesApi.list(), 'dashboard_employees')
            const empRaw = empResult.data?.items || empResult.data || []
            const mapped = (Array.isArray(empRaw) ? empRaw : [])
                .filter(e => e.status !== 'terminated')
                .map(e => ({
                    id: e.id,
                    name: displayName(e.full_name),
                    dept: e.department_name || e.department || '—',
                }))
            if (mapped.length > 0) setEmployeesList(mapped)
        } catch { /* fallback на overview */ }

        setDataSource(mainSource)
        setLoading(false)
        setRefreshing(false)
    }, [birthdayWindow])

    useEffect(() => { loadData() }, [loadData])

    const handleRefresh = useCallback(() => {
        setRefreshing(true)
        loadData()
    }, [loadData])

    // ── Документы ──
    const docsEnriched = useMemo(() => docsList.map(doc => {
        const empId = doc.employeeId || doc.employee_id
        const emp = employeesList.find(e => e.id === empId)
        const expiresAt = doc.expiresAt || doc.expires_at
        return {
            ...doc,
            empId,
            empName: emp?.name || doc.employee_name || '?',
            dept: emp?.dept || doc.department || '',
            days: daysUntil(expiresAt),
            expiresAt,
            type: doc.type || 'other',
        }
    }), [docsList, employeesList])

    const expiredDocs = docsEnriched.filter(d => d.days < 0)
    const urgentDocs = docsEnriched.filter(d => d.days >= 0 && d.days <= 30)
    const week1Docs = docsEnriched.filter(d => d.days >= 0 && d.days <= 7)
    const topUrgent = useMemo(() => [...docsEnriched].sort((a, b) => a.days - b.days).slice(0, 6), [docsEnriched])

    const totals = overview?.totals || {}
    const departments = overview?.departments || []
    const probationByDept = overview?.probation_by_department || []
    const probationTotal = useMemo(
        () => probationByDept.reduce((acc, d) => acc + (d.employees?.length || 0), 0),
        [probationByDept],
    )
    const maxDeptTotal = useMemo(
        () => departments.reduce((m, d) => Math.max(m, d.total || 0), 0) || 1,
        [departments],
    )

    const birthdayItems = birthdays?.items || []
    const birthdaysThisWeek = useMemo(
        () => birthdayItems.filter(b => b.days_until <= 7).length,
        [birthdayItems],
    )
    const birthdaysToday = birthdays?.today_count || 0

    // Группировка ДР по неделям для календарного представления.
    const birthdayGroups = useMemo(() => {
        const groups = { today: [], thisWeek: [], later: [] }
        birthdayItems.forEach(b => {
            if (b.days_until === 0) groups.today.push(b)
            else if (b.days_until <= 7) groups.thisWeek.push(b)
            else groups.later.push(b)
        })
        return groups
    }, [birthdayItems])

    if (loading) {
        return (
            <div className="dashboard-loading">
                <div className="text-center">
                    <LoadingIcon status="PROCESSING" size={32} />
                    <div className="text-sm text-muted mt-12">Загрузка данных...</div>
                </div>
            </div>
        )
    }

    return (
        <div className="page-content" style={{ display: 'flex', flexDirection: 'column' }}>
            <div className="flex justify-between items-start mb-16" style={{ gap: 12, flexWrap: 'wrap' }}>
                <div>
                    <p className="text-rel-sm-2 text-muted" style={{ margin: 0 }}>
                        Сотрудники, испытательные сроки и дни рождения команды.
                    </p>
                </div>
                <div className="flex items-center gap-8">
                    <button
                        className="btn btn--toolbar btn--sm"
                        onClick={handleRefresh}
                        disabled={refreshing}
                        title="Обновить все данные"
                    >
                        <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} /> Обновить
                    </button>
                    <button
                        className="btn btn--primary btn--sm"
                        onClick={() => navigate('/employees/new')}
                    >
                        <Plus size={14} /> Новый сотрудник
                    </button>
                </div>
            </div>

            {AI_BRIEF_ROLES.includes(user?.role) && <AiDailyBriefWidget />}

            {dataSource === 'offline' && <OfflineBanner onRefresh={loadData} loading={loading} />}
            {dataSource === 'cache' && (
                <div className="dashboard-banner dashboard-banner--cache">
                    <Clock size={12} />
                    <span>Данные из кэша (бэкенд недоступен)</span>
                    <button onClick={handleRefresh} className="dashboard-banner__action">
                        <RefreshCw size={10} /> Обновить
                    </button>
                </div>
            )}

            {/* KPI: HR-focused */}
            {/* DS Excellence Tier §19: entrance-каскад KPI-ряда при монтировании. */}
            <div
                className="grid gap-12 mb-16 animate-rise-in stagger-children"
                style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}
            >
                <KpiTile
                    icon={Users}
                    label="Всего сотрудников"
                    value={totals.total ?? '—'}
                    hint={totals.active != null ? `активных: ${totals.active}` : null}
                    tone="info"
                />
                <KpiTile
                    icon={GraduationCap}
                    label="На испытательном"
                    value={totals.probation ?? '—'}
                    hint={probationByDept.length > 0 ? `в ${probationByDept.length} отделах` : 'нет'}
                    tone={(totals.probation ?? 0) > 0 ? 'warning' : 'neutral'}
                />
                <KpiTile
                    icon={Cake}
                    label="ДР на этой неделе"
                    value={birthdaysThisWeek}
                    hint={birthdaysToday > 0 ? `сегодня: ${birthdaysToday}` : `всего в окне: ${birthdayItems.length}`}
                    tone={birthdaysThisWeek > 0 ? 'success' : 'neutral'}
                />
                <KpiTile
                    icon={AlertTriangle}
                    label="Документы критично"
                    value={expiredDocs.length + urgentDocs.length}
                    tone={(expiredDocs.length + urgentDocs.length) > 0 ? 'danger' : undefined}
                    hint={expiredDocs.length > 0 ? `${expiredDocs.length} просрочено` : 'в порядке'}
                />
            </div>

            {/* ── Сотрудники по отделам — карточками (перемещён вниз дашборда, #30) ── */}
            <div style={{ order: 90 }}>
            <Widget
                title="Сотрудники по отделам"
                badge={totals.total != null ? `${totals.total} чел. · ${departments.length} отделов` : null}
            >
                {departments.length > 0 ? (
                    <div
                        className="grid gap-10 animate-rise-in"
                        style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}
                    >
                        {departments.map(d => {
                            const pct = Math.round(((d.total || 0) / maxDeptTotal) * 100)
                            return (
                                <button
                                    key={d.department_id || '__unassigned__'}
                                    type="button"
                                    onClick={() => d.department_id && navigate(`/orgchart?dept=${d.department_id}`)}
                                    className="dept-card hover-lift"
                                    style={{
                                        textAlign: 'left',
                                        padding: 12,
                                        borderRadius: 10,
                                        border: '1px solid var(--color-border)',
                                        background: 'var(--color-bg-card)',
                                        cursor: d.department_id ? 'pointer' : 'default',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 8,
                                    }}
                                >
                                    <div className="flex items-center justify-between gap-8">
                                        <div className="flex items-center gap-6" style={{ minWidth: 0, flex: 1 }}>
                                            <Building2 size={14} className="color-blue flex-shrink-0" />
                                            <span
                                                className="text-sm fw-700 color-text"
                                                style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                                title={d.department_name}
                                            >
                                                {d.department_name}
                                            </span>
                                        </div>
                                        <span className="text-xl fw-800 color-text">{d.total}</span>
                                    </div>
                                    {d.head_name && (
                                        <div className="text-xs text-muted" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={`Руководитель: ${displayName(d.head_name)}`}>
                                            Руководитель: {displayName(d.head_name)}
                                        </div>
                                    )}
                                    <div
                                        style={{
                                            height: 6,
                                            borderRadius: 3,
                                            background: 'var(--color-bg-elevated)',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: `${Math.max(pct, 5)}%`,
                                                height: '100%',
                                                background: 'var(--color-accent)',
                                            }}
                                        />
                                    </div>
                                    <div className="flex items-center gap-10 text-xxs text-muted" style={{ flexWrap: 'wrap' }}>
                                        {d.active > 0 && <span>● Активных: {d.active}</span>}
                                        {d.probation > 0 && (
                                            <span style={{ color: 'var(--color-warning)' }}>● ИС: {d.probation}</span>
                                        )}
                                        {d.on_leave > 0 && (
                                            <span style={{ color: 'var(--color-info)' }}>● В отпуске: {d.on_leave}</span>
                                        )}
                                        {d.suspended > 0 && (
                                            <span style={{ color: 'var(--color-danger)' }}>● Отстранён: {d.suspended}</span>
                                        )}
                                    </div>
                                </button>
                            )
                        })}
                    </div>
                ) : (
                    <div className="p-16 text-center text-muted text-sm">
                        Нет данных по отделам
                    </div>
                )}
            </Widget>
            </div>

            {/* ── На испытательном по отделам + Дни рождения ── */}
            <div
                className="grid gap-12 mt-20"
                style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))' }}
            >
                <Widget
                    title="На испытательном сроке"
                    badge={probationTotal > 0
                        ? `${probationTotal} чел. в ${probationByDept.length} отд.`
                        : 'нет'}
                >
                    {probationByDept.length > 0 ? (
                        <div className="col-gap-12">
                            {probationByDept.map(group => (
                                <div key={group.department_id || '__unassigned__'}>
                                    <div className="flex items-center justify-between mb-6">
                                        <div className="flex items-center gap-6">
                                            <Building2 size={12} className="color-blue" />
                                            <span className="text-sm fw-700 color-text">{group.department_name}</span>
                                        </div>
                                        <span className="text-xs text-muted">{group.employees.length} чел.</span>
                                    </div>
                                    <div className="col-gap-6" style={{ paddingLeft: 18 }}>
                                        {group.employees.map(e => (
                                            <button
                                                key={e.id}
                                                type="button"
                                                onClick={() => navigate(`/employees/${e.id}`)}
                                                className="flex items-center justify-between gap-8"
                                                style={{
                                                    padding: '6px 10px',
                                                    border: '1px solid var(--color-border)',
                                                    background: 'var(--color-bg-elevated)',
                                                    borderRadius: 8,
                                                    cursor: 'pointer',
                                                    textAlign: 'left',
                                                }}
                                            >
                                                <div style={{ minWidth: 0, flex: 1 }}>
                                                    <div className="text-sm fw-600 color-text" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {displayName(e.full_name)}
                                                    </div>
                                                    <div className="text-xxs text-muted" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {e.position || '—'}
                                                        {e.probation_end_date && ` · до ${formatDate(e.probation_end_date)}`}
                                                    </div>
                                                </div>
                                                {e.days_left != null && (
                                                    <span
                                                        className="text-xxs fw-700"
                                                        style={{
                                                            padding: '2px 8px',
                                                            borderRadius: 999,
                                                            background: e.days_left <= 3 ? 'color-mix(in srgb, var(--color-danger) 8%, transparent)' : 'color-mix(in srgb, var(--color-warning) 8%, transparent)',
                                                            color: e.days_left <= 3 ? 'var(--color-danger)' : 'var(--color-warning)',
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        {e.days_left <= 0 ? 'сегодня' : `${e.days_left} дн`}
                                                    </span>
                                                )}
                                                <ChevronRight size={12} className="text-muted flex-shrink-0" />
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="p-16 text-center text-muted text-sm">
                            Никто не на испытательном сроке
                        </div>
                    )}
                </Widget>

                <Widget
                    title="Дни рождения"
                    badge={birthdayItems.length > 0
                        ? `${birthdayItems.length} в ближайшие ${birthdayWindow} дн.`
                        : 'нет'}
                >
                    <div className="flex items-center justify-end gap-6 mb-10">
                        <label className="text-xxs text-muted">Окно:</label>
                        <select
                            value={birthdayWindow}
                            onChange={e => setBirthdayWindow(+e.target.value)}
                            style={{
                                padding: '2px 6px',
                                fontSize: '0.75rem',
                                border: '1px solid var(--color-border)',
                                borderRadius: 6,
                                background: 'var(--color-bg-elevated)',
                            }}
                        >
                            <option value={7}>7 дней</option>
                            <option value={14}>14 дней</option>
                            <option value={30}>30 дней</option>
                            <option value={60}>60 дней</option>
                            <option value={90}>90 дней</option>
                        </select>
                    </div>
                    {birthdayItems.length > 0 ? (
                        <div className="col-gap-12">
                            {birthdayGroups.today.length > 0 && (
                                <BirthdayGroup
                                    label="Сегодня"
                                    items={birthdayGroups.today}
                                    onItemClick={(id) => navigate(`/employees/${id}`)}
                                    tone="today"
                                />
                            )}
                            {birthdayGroups.thisWeek.length > 0 && (
                                <BirthdayGroup
                                    label="На этой неделе"
                                    items={birthdayGroups.thisWeek}
                                    onItemClick={(id) => navigate(`/employees/${id}`)}
                                    tone="soon"
                                />
                            )}
                            {birthdayGroups.later.length > 0 && (
                                <BirthdayGroup
                                    label="Позже"
                                    items={birthdayGroups.later}
                                    onItemClick={(id) => navigate(`/employees/${id}`)}
                                />
                            )}
                        </div>
                    ) : (
                        <div className="p-16 text-center text-muted text-sm">
                            <CalendarIcon size={20} className="mb-8" />
                            <div>В окне {birthdayWindow} дн. нет именинников.</div>
                            {birthdays?.nearest_days_until != null && (
                                <div className="text-xxs mt-8">
                                    Ближайший ДР через {birthdays.nearest_days_until} дн.{' '}
                                    {birthdays.nearest_days_until > birthdayWindow && (
                                        <button
                                            className="btn btn--secondary btn--sm"
                                            onClick={() => {
                                                const target = birthdays.nearest_days_until <= 60 ? 60 : 90
                                                setBirthdayWindow(target)
                                            }}
                                            style={{ padding: '2px 6px', marginLeft: 4 }}
                                        >
                                            расширить окно
                                        </button>
                                    )}
                                </div>
                            )}
                            {birthdays?.nearest_days_until == null && (
                                <div className="text-xxs mt-8">
                                    У сотрудников не заполнено поле «Дата рождения».
                                </div>
                            )}
                        </div>
                    )}
                </Widget>
            </div>

            {/* ── Срочные документы (визы/пермиты) ── */}
            {topUrgent.length > 0 ? (
                <Widget
                    className="mt-20"
                    title="Визы и пермиты — срочные"
                    badge={`${expiredDocs.length + urgentDocs.length} требуют действий`}
                    badgeColor="var(--color-danger)"
                >
                    <div className="col-gap-8">
                        {topUrgent.map(doc => (
                            <button
                                key={doc.id}
                                type="button"
                                data-urgency={urgencyOf(doc.days)}
                                className="dashboard-doc-row"
                                onClick={() => doc.empId && navigate(`/employees/${doc.empId}?section=visas`)}
                                style={{ cursor: doc.empId ? 'pointer' : 'default', width: '100%', textAlign: 'left' }}
                                title="Открыть визы/пермиты сотрудника"
                            >
                                <div className="flex-1">
                                    <div className="dashboard-doc-row__title">{shortName(doc.empName)}</div>
                                    <div className="dashboard-doc-row__meta">
                                        <span>{typeLabelOf(doc.type)}</span>
                                        <span>•</span>
                                        <span>{doc.dept}</span>
                                        <span>•</span>
                                        <span>до {formatDate(doc.expiresAt)}</span>
                                    </div>
                                </div>
                                <span className="dashboard-doc-row__pill">{statusTextOf(doc.days)}</span>
                            </button>
                        ))}
                    </div>
                </Widget>
            ) : (
                <Widget className="mt-20" title="Визы и пермиты">
                    <div className="p-16 text-center text-muted text-sm inline-flex items-center justify-center gap-6">
                        <CheckCircle size={14} /> Нет документов, требующих действий.
                    </div>
                </Widget>
            )}

            {/* ── Сводка по документам ── */}
            <div
                className="grid gap-12 mt-20"
                style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}
            >
                <Widget title="Документы по статусу">
                    <div className="col-gap-8">
                        {[
                            { label: 'Всего', value: docsList.length, tone: 'accent', icon: FileText },
                            { label: 'Просрочено', value: expiredDocs.length, tone: 'danger', icon: XCircle },
                            { label: '≤7 дней', value: week1Docs.length, tone: 'danger', icon: AlertTriangle },
                            { label: '≤30 дней', value: urgentDocs.length, tone: 'warning', icon: AlertCircle },
                            { label: 'В порядке', value: Math.max(0, docsList.length - expiredDocs.length - urgentDocs.length), tone: 'success', icon: CheckCircle },
                        ].map(item => (
                            <div className="dashboard-stat-row" key={item.label}>
                                <span className="dashboard-stat-row__label flex items-center gap-6">
                                    {item.icon && <item.icon size={14} className={`color-${item.tone}`} />}
                                    {item.label}
                                </span>
                                <span className="dashboard-stat-row__value" data-tone={item.tone}>{item.value}</span>
                            </div>
                        ))}
                    </div>
                </Widget>
                <Widget title="Быстрые действия">
                    <div className="col-gap-6">
                        <QuickAction Icon={Users} label="Все сотрудники" onClick={() => navigate('/employees')} />
                        <QuickAction Icon={Building2} label="Оргструктура" onClick={() => navigate('/orgchart')} />
                        <QuickAction Icon={FileText} label="Документы" onClick={() => navigate('/documents')} />
                        <QuickAction Icon={GraduationCap} label="HR Lifecycle" onClick={() => navigate('/hr-lifecycle')} />
                    </div>
                </Widget>
            </div>
        </div>
    )
}

function QuickAction({ Icon, label, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="flex items-center justify-between gap-8"
            style={{
                padding: '8px 12px',
                border: '1px solid var(--color-border)',
                background: 'var(--color-bg-elevated)',
                borderRadius: 8,
                cursor: 'pointer',
                textAlign: 'left',
            }}
        >
            <span className="flex items-center gap-8 text-sm fw-600 color-text">
                <Icon size={14} className="color-blue" />
                {label}
            </span>
            <ArrowRight size={12} className="text-muted" />
        </button>
    )
}

function BirthdayGroup({ label, items, onItemClick, tone }) {
    const isToday = tone === 'today'
    const isSoon = tone === 'soon'
    return (
        <div>
            <div className="text-xxs fw-700 text-muted mb-6" style={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                {label}
            </div>
            <div className="col-gap-6">
                {items.map(b => {
                    const _age = turningAge(b.date_of_birth, b.days_until)
                    const _milestone = isMilestoneAge(_age)
                    return (
                    <button
                        key={b.id}
                        type="button"
                        onClick={() => onItemClick?.(b.id)}
                        className="flex items-center gap-10"
                        style={{
                            padding: '8px 10px',
                            border: `1px solid ${_milestone ? 'var(--color-accent)' : (isToday ? 'color-mix(in srgb, var(--color-danger) 21%, transparent)' : 'var(--color-border)')}`,
                            background: isToday ? 'color-mix(in srgb, var(--color-danger) 3%, transparent)' : 'var(--color-bg-elevated)',
                            borderRadius: 8,
                            cursor: 'pointer',
                            textAlign: 'left',
                        }}
                    >
                        {b.avatar_url ? (
                            <img
                                src={b.avatar_url}
                                alt={displayName(b.full_name)}
                                style={{
                                    width: 34, height: 34, borderRadius: '50%', objectFit: 'cover',
                                    border: '2px solid var(--color-border)',
                                }}
                            />
                        ) : (
                            <div
                                className="flex items-center justify-center fw-700 color-white"
                                style={{
                                    width: 34, height: 34, borderRadius: '50%',
                                    background: isToday ? 'var(--color-danger)' : 'var(--color-accent)',
                                    fontSize: 12,
                                }}
                            >
                                {getInitials(b.full_name)}
                            </div>
                        )}
                        <div style={{ minWidth: 0, flex: 1 }}>
                            <div className="text-sm fw-700 color-text" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {displayName(b.full_name)}
                            </div>
                            <div className="text-xxs text-muted" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {b.position || '—'} {b.department_name ? ` · ${b.department_name}` : ''}
                            </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <div
                                className="text-xs fw-700"
                                style={{ color: isToday ? 'var(--color-danger-text)' : (isSoon ? 'var(--color-warning)' : 'var(--color-text)') }}
                            >
                                {formatBirthdayLabel(b.days_until)}
                            </div>
                            {_milestone && (
                                <div className="text-xxs fw-700 inline-flex items-center gap-2" style={{ color: 'var(--color-accent)' }}>
                                    <Cake size={10} /> юбилей · {_age}
                                </div>
                            )}
                            <div className="text-xxs text-muted">{formatBirthdayDate(b.birthday_md)}</div>
                        </div>
                    </button>
                    )
                })}
            </div>
        </div>
    )
}
